import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { INK, ACCENT } from "../colors";

// Frame timing @ 30fps:
//   15  → review notification bubble starts revealing
//   60  → suggestion bubble + action buttons reveal
//  140  → "Approve & post" button press animation peaks
//  170  → posted-confirmation bubble reveals
//  330  → loop end (≈11s)

const BUBBLE_IN = "#E9E9EB";

const SmsBubble: React.FC<{
  opacity: number;
  translateY: number;
  children: React.ReactNode;
  bg?: string;
  maxWidth?: number;
}> = ({ opacity, translateY, children, bg = BUBBLE_IN, maxWidth = 240 }) => (
  <div
    style={{
      opacity,
      transform: `translateY(${translateY}px)`,
      backgroundColor: bg,
      color: "#000",
      borderRadius: "18px 18px 18px 4px",
      padding: "9px 13px",
      maxWidth,
      marginBottom: 8,
      alignSelf: "flex-start",
    }}
  >
    {children}
  </div>
);

const ActionButton: React.FC<{
  primary?: boolean;
  pressed?: number;
  children: React.ReactNode;
  color?: string;
  bg?: string;
}> = ({ primary, pressed = 0, children, color, bg }) => {
  const baseBg = bg ?? (primary ? "#22c55e" : "transparent");
  const activeBg = primary ? "#16a34a" : "rgba(0,0,0,0.05)";
  const txtColor = color ?? (primary ? "#fff" : INK[700]);

  return (
    <div
      style={{
        margin: "0 8px 4px",
        padding: "8px 12px",
        borderRadius: 8,
        textAlign: "center",
        fontSize: 13,
        fontWeight: primary ? 700 : 600,
        color: txtColor,
        backgroundColor: pressed > 0 ? activeBg : baseBg,
        transform: `scale(${interpolate(pressed, [0, 1], [1, 0.96])})`,
        transition: "background-color 0.1s",
      }}
    >
      {children}
    </div>
  );
};

export const SmsApprovalAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const review = spring({ frame: frame - 15, fps, config: { damping: 14 } });
  const suggestion = spring({
    frame: frame - 60,
    fps,
    config: { damping: 14 },
  });
  const confirmation = spring({
    frame: frame - 170,
    fps,
    config: { damping: 14 },
  });

  const pressed = interpolate(
    frame,
    [135, 142, 150],
    [0, 1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#f6f6f6",
        padding: "8px 12px 24px",
        flexDirection: "column",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "#8a8a8e",
          textAlign: "center",
          margin: "6px 0 6px",
          letterSpacing: 0.2,
        }}
      >
        Today 6:47 PM
      </div>

      {/* Stage 1: review notification */}
      <SmsBubble
        opacity={review}
        translateY={interpolate(review, [0, 1], [10, 0])}
      >
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
          OneTapReply
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.3 }}>
          New ★★★★★ from Sarah K. at <strong>Pine Street Café</strong>:
          <br />
          <span style={{ color: INK[700] }}>
            &ldquo;Best flat white in town! Charlotte was so welcoming.&rdquo;
          </span>
        </div>
      </SmsBubble>

      {/* Stage 2: suggested reply + 3 action buttons */}
      <SmsBubble
        opacity={suggestion}
        translateY={interpolate(suggestion, [0, 1], [10, 0])}
        maxWidth={258}
      >
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
          Suggested reply
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.3 }}>
          &ldquo;Thank you, Sarah! We&apos;ll let Charlotte know — she&apos;ll
          be thrilled. See you next time! ☕&rdquo;
        </div>

        <div
          style={{
            margin: "10px -8px -4px",
            paddingTop: 8,
            borderTop: "1px solid rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <ActionButton primary pressed={pressed}>
            ✓  Approve &amp; post
          </ActionButton>
          <ActionButton color={INK[700]}>Edit before posting</ActionButton>
          <ActionButton color={INK[500]}>Skip</ActionButton>
        </div>
      </SmsBubble>

      {/* Stage 3: posted confirmation */}
      {frame > 150 && (
        <SmsBubble
          opacity={confirmation}
          translateY={interpolate(confirmation, [0, 1], [10, 0])}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 2,
              color: ACCENT[600],
            }}
          >
            Posted to Google ✓
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.3, color: INK[700] }}>
            Live in 7 seconds. Sarah will see your reply on her review.
          </div>
        </SmsBubble>
      )}
    </AbsoluteFill>
  );
};
